import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonDialoguesService } from '@flyfreely-portal-ui/common-dialogues';
import {
    AttachmentHandler,
    CurrentPersonDto,
    DO_NOTHING,
    FlyFreelyError,
    FlyFreelyLoggingService,
    LoggedInUser,
    PersonService,
    PersonWithRoles,
    PersonsOrganisationDto,
    TaskCategory,
    UserService,
    UserStatus,
    WorkTracker
} from '@flyfreely-portal-ui/flyfreely';
import {
    NgSelectPaginationValues,
    NgSelectSearch,
    ngSelectPreSearchConfigure,
    updateNgSelectData
} from '@flyfreely-portal-ui/ng-select';
import { personSearch } from '@flyfreely-portal-ui/ui';
import { LinkAction } from 'libs/attachments/src/lib/link-manager/link-manager.interface';
import { AttachmentHandlerService } from 'libs/comments/src/lib/attachment-handler.service';
import { CommentsComponent } from 'libs/comments/src/lib/comments/comments.component';
import { Comment } from 'libs/comments/src/lib/interfaces';
import {
    CreateTaskCommand,
    TaskPriority
} from 'libs/flyfreely/src/lib/services/generalTasks.service';
import {
    GeneralTasksDataService,
    formatGeneralTasksPriority
} from 'libs/outstanding-tasks/src/lib/widgets/general-task/general-tasks-data.service';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { Delta } from 'quill';
import { Subject, firstValueFrom, take } from 'rxjs';
import { debounceTime, filter, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'general-task-add-dialogue',
    templateUrl: './general-task-add-dialogue.component.html',
    styleUrls: ['./general-task-add-dialogue.component.css']
})
export class GeneralTaskAddDialogueComponent implements OnInit, OnDestroy {
    @ViewChild(CommentsComponent) uiComments: CommentsComponent;
    @Input() organisation: PersonsOrganisationDto;
    @Input() category: TaskCategory = TaskCategory.GENERAL;

    taskId: number;
    attachmentsHandler: AttachmentHandler;
    taskNewForm: FormGroup;
    comments: Comment[];
    currentUser: CurrentPersonDto | undefined;

    personList: PersonWithRoles[] = [];
    loadingPersonnel = false;

    private personSearchSource = new Subject<NgSelectSearch>();

    personSelectConfig: NgSelectPaginationValues = {
        currentPage: 0,
        preSearchPage: null,
        searchValue: null,
        totalItems: null
    };

    placeholderText =
        'Add a comment. You can tag people with "@", RPA with "#RPA", and batteries with "#BATTERY"';

    personSearch = personSearch.bind(this);
    priorityOptions = Object.entries(formatGeneralTasksPriority).map(
        ([value, label]) => ({ label, value })
    );
    readonly LinkAction = LinkAction;

    working = false;
    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();
    constructor(
        private modal: BsModalRef<GeneralTaskAddDialogueComponent>,
        private personService: PersonService,
        private userService: UserService,
        private generalTasksDataService: GeneralTasksDataService,
        private logging: FlyFreelyLoggingService,
        private commonDialoguesService: CommonDialoguesService,
        private attachmentHandlerService: AttachmentHandlerService,
        modalOptions: ModalOptions
    ) {
        modalOptions.closeInterceptor = () => {
            if (this.taskNewForm.dirty) {
                return commonDialoguesService.showConfirmationDialogue(
                    'Cancel Create Task',
                    'You have unsaved changes, are you sure you want to cancel?',
                    'Yes',
                    () =>
                        firstValueFrom(
                            this.generalTasksDataService.deleteTask(this.taskId)
                        )
                );
            }
            return Promise.resolve();
        };
    }

    ngOnInit(): void {
        this.taskNewForm = new FormGroup({
            taskName: new FormControl('', Validators.required),
            details: new FormControl('', Validators.required),
            dueDate: new FormControl(),
            taskPriority: new FormControl(
                TaskPriority.MEDIUM,
                Validators.required
            ),
            assignee: new FormControl(null),
            reporter: new FormControl({
                value: null,
                disabled: true
            })
        });

        this.generalTasksDataService
            .newTask(this.organisation.id, this.category)
            .subscribe(newTask => {
                this.taskId = newTask.id;
                this.attachmentsHandler =
                    this.generalTasksDataService.attachmentHandler(
                        this.taskId,
                        this.organisation.id
                    );
                this.attachmentHandlerService.attachmentHandler =
                    this.attachmentsHandler;
                this.refreshComments();
                this.taskNewForm.markAsDirty();
            });

        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => {
                this.working = working;
            });

        this.generalTasksDataService.commentChange$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(() => {
                this.refreshComments();
            });

        this.personSearchSource
            .pipe(debounceTime(300), takeUntil(this.ngUnsubscribe$))
            .subscribe(search => {
                this.personSelectConfig = ngSelectPreSearchConfigure(
                    search,
                    this.personSelectConfig
                );
                this.refreshPersonList();
            });

        this.initializeCurrentUser();
    }

    ngOnDestroy() {
        this.personSearchSource.complete();
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private initializeCurrentUser() {
        this.userService.userChange$
            .pipe(
                filter<LoggedInUser>(c => c.type === UserStatus.LOGGED_IN),
                take(1),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(changes => {
                this.currentUser = changes.currentUser;

                if (this.currentUser?.id) {
                    this.refreshPersonList([this.currentUser.id]);
                } else {
                    this.refreshPersonList();
                }
            });
    }

    private refreshPersonList(preloadIds?: number[]) {
        this.loadingPersonnel = true;
        this.personService
            .findPersonnelGraphQL(
                this.organisation.id,
                this.personSelectConfig.currentPage,
                15,
                {
                    textSearch:
                        this.personSelectConfig.searchValue || undefined,
                    idList: preloadIds
                }
            )
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: result => {
                    const newPeople = result.results.filter(
                        p =>
                            this.personList.findIndex(
                                person => person.id === p.id
                            ) === -1
                    );
                    this.personList = this.personList.concat(newPeople);

                    if (preloadIds == null) {
                        this.personSelectConfig = updateNgSelectData(
                            result,
                            this.personSelectConfig
                        );
                    } else {
                        this.setInitialReporter();
                    }

                    this.loadingPersonnel = false;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading personnel: ${error.message}`
                    );
                    this.loadingPersonnel = false;
                }
            });
    }

    private setInitialReporter() {
        if (this.taskNewForm.controls.reporter.value) {
            return;
        }

        if (!this.currentUser?.id) {
            return;
        }

        const reporterPerson = this.personList.find(
            person => person.id === this.currentUser.id
        );

        if (reporterPerson) {
            this.taskNewForm.controls.reporter.patchValue(reporterPerson);
        }
    }

    onPersonSelectScroll() {
        const resultLength =
            this.personSelectConfig.searchValue == null
                ? this.personList.length
                : this.personList.filter(person =>
                      this.personSearch(
                          this.personSelectConfig.searchValue,
                          person
                      )
                  ).length;

        if (
            this.personSelectConfig.totalItems == null ||
            resultLength !== this.personSelectConfig.totalItems
        ) {
            this.refreshPersonList();
        }
    }

    onPersonSelectOpen() {
        if (
            this.personSelectConfig.totalItems == null ||
            this.personList.length !== this.personSelectConfig.totalItems
        ) {
            this.refreshPersonList();
        }
    }

    onPersonSelectSearch(search: NgSelectSearch) {
        this.personSearchSource.next(search);
    }

    onPersonSelectClose() {
        this.personSelectConfig.searchValue = null;
    }

    refreshComments() {
        this.generalTasksDataService
            .findComments(this.taskId)
            .subscribe(res => {
                this.comments = res as unknown as Comment[];
            });
    }

    onCancel() {
        this.commonDialoguesService
            .showConfirmationDialogue(
                'Cancel Create Task',
                'You have unsaved changes, are you sure you want to cancel?',
                'Yes',
                () =>
                    firstValueFrom(
                        this.generalTasksDataService
                            .deleteTask(this.taskId)
                            .pipe(
                                tap({
                                    error: (error: FlyFreelyError) =>
                                        this.logging.error(
                                            error,
                                            `Error cancelling Task creation: ${error.message}`
                                        )
                                })
                            )
                    )
            )
            .then(() => {
                this.taskNewForm.reset();
                this.taskNewForm.markAsPristine();
                this.modal.hide();
            }, DO_NOTHING);
    }

    onAddTask() {
        const value = this.taskNewForm.value;
        const toAdd: CreateTaskCommand = {
            taskId: this.taskId,
            summary: value.taskName,
            description: value.details,
            dueDate: value.dueDate,
            assigneeId: value.assignee,
            priority: value.taskPriority
        };
        this.createComment();
        this.generalTasksDataService.createTask(toAdd).subscribe({
            next: () => {
                this.logging.success(`Created ${value.taskName}`);
                this.taskNewForm.markAsPristine();
                this.modal.hide();
            },
            error: (error: FlyFreelyError) => {
                this.logging.error(
                    error,
                    `Error while creating task: ${error.message}`
                );
            }
        });
    }

    createComment() {
        const commentContent = this.uiComments.richTextEditor.content;
        if (!commentContent) {
            return;
        }
        this.generalTasksDataService
            .createComment(this.taskId, commentContent as Delta)
            .subscribe({
                next: () => {
                    this.logging.success(`Comment added`);
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error while adding comment: ${error.message}`
                    );
                }
            });
    }
}
